/**
 * Copyright (c) 2019-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { styled } from "@ndla/styled-system/jsx";
import type { StyledProps } from "@ndla/styled-system/types";
import { forwardRef, useContext, type RefObject } from "react";
import { Link, type LinkProps } from "react-router";
import { LinkPathContext, type LinkPathResolver } from "./LinkPathContext";
import { MissingRouterContext } from "./MissingRouterContext";

const oldNdlaRegex = /(.*)\/?node\/(\d+).*/;

const isExternalLink = (to?: LinkProps["to"]) =>
  typeof to === "string" &&
  (to.startsWith("https://") || to.startsWith("http://") || to.startsWith("mailto:") || to.endsWith(".xml"));

export const isOldNdlaLink = (to?: LinkProps["to"]) => typeof to === "string" && to.match(oldNdlaRegex) !== null;

const resolveTarget = (to: LinkProps["to"] | undefined, resolve: LinkPathResolver): LinkProps["to"] | undefined => {
  if (typeof to === "string") {
    return to.startsWith("/") && !isExternalLink(to) && !isOldNdlaLink(to) ? resolve(to) : to;
  }
  return to?.pathname?.startsWith("/") ? { ...to, pathname: resolve(to.pathname) } : to;
};

export interface SafeLinkProps extends LinkProps, StyledProps {
  ref?: RefObject<HTMLAnchorElement | null>;
  asAnchor?: boolean;
  disabled?: boolean;
  unstyled?: boolean;
}

const StyledLink = styled(Link, {}, { baseComponent: true });

// Fallback to normal link if app is missing RouterContext, link is external or is old ndla link

export const SafeLink = forwardRef<HTMLAnchorElement, SafeLinkProps>(
  ({ to, replace, state, disabled, unstyled, children, tabIndex, asAnchor, reloadDocument, ...rest }, ref) => {
    const isMissingRouterContext = useContext(MissingRouterContext);
    const resolveLinkPath = useContext(LinkPathContext);
    const unstyledProps = unstyled ? { "data-unstyled": "" } : {};
    const resolvedTarget = resolveTarget(to, resolveLinkPath);

    if (isMissingRouterContext || isExternalLink(to) || isOldNdlaLink(to) || asAnchor || disabled) {
      const href = typeof resolvedTarget === "string" ? resolvedTarget : "#";
      return (
        <styled.a
          href={disabled ? undefined : href}
          role={disabled ? "link" : undefined}
          aria-disabled={disabled}
          ref={ref}
          tabIndex={tabIndex}
          {...unstyledProps}
          {...rest}
        >
          {children}
        </styled.a>
      );
    }

    return (
      // RR6 link immediately fails if to is somehow undefined, so we provide an empty fallback to recover.
      <StyledLink
        ref={ref}
        tabIndex={tabIndex ?? 0}
        to={resolvedTarget ?? ""}
        state={state}
        reloadDocument={reloadDocument}
        replace={replace}
        {...unstyledProps}
        {...rest}
      >
        {children}
      </StyledLink>
    );
  },
);
